/* eslint-disable no-extend-native */
import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import propTypes from "prop-types";
import classnames from "classnames";

import "./Form.scss";
import { createDataAttributes } from "../../utilities";

const deleteProperty = (property, obj) => {
  // Needs to be immutable version so we don't delete property on original reference.
  // This version appears to be fastest way to do an immutable delete
  const next = { ...obj };
  delete next[property];
  return next;
};

export const FormContext = React.createContext(null);

export const Form = ({ children, context, className, dataAttrib, ...rest }) => {
  return (
    <FormContext.Provider value={context}>
      <form
        className={classnames("uikit__form", className)}
        onSubmit={context.handleSubmit}
        {...createDataAttributes(dataAttrib)}
        {...rest}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
};

Form.propTypes = {
  context: propTypes.any,
  children: propTypes.any,
  className: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object)
};

Form.defaultProps = {};

/*
-------------
   useForm
-------------
*/

export const useForm = props => {
  // We may need to change the internal representation of values (and possibly touched / errors)
  // so that values is a ref that we add the fields to. I think this will result in
  // fewer re-renders to other fields that didn't change.
  const [values, setValues] = useState(props.values || {});
  const [errors, setErrors] = useState(props.errors || {});
  const [invalidFields, setInvalidFields] = useState([]);

  // We need a way to have the validation function for each input.
  // Inputs will use the useField hook to register with this context.
  const fieldsRef = useRef({});
  const registerField = useCallback(
    ({ field, validation, onValidate, label }) => {
      fieldsRef.current[field] = { validation, onValidate, label };
    },
    [fieldsRef]
  );
  const unregisterField = useCallback(
    ({ field }) => {
      delete fieldsRef.current[field];
    },
    [fieldsRef]
  );

  const removeField = useCallback(({ field }) => {
    setValues(prev => deleteProperty(field, prev));
    setErrors(prev => deleteProperty(field, prev));
    delete fieldsRef.current[field];
  }, []);

  const validateForm = useCallback(() => {
    const formErrors = Object.keys(fieldsRef.current).reduce((acc, field) => {
      const fieldRef = fieldsRef.current[field];
      // Probably needs to support async / promise validation in case api call needs made
      // to perform validation.
      const error = fieldRef.onValidate({
        field,
        value: values[field],
        validation: fieldRef.validation
      });

      if (error.length) {
        acc[field] = error;
      }

      return acc;
    }, {});

    setErrors(formErrors);

    const isValid =
      Object.values(formErrors).filter(ar => ar.length > 0).length === 0;

    return isValid;
  }, [fieldsRef, values]);

  const validateFields = useCallback(
    fields => {
      const formErrors = Object.keys(fieldsRef.current)
        .filter(f => fields.includes(f))
        .reduce((acc, field) => {
          const fieldRef = fieldsRef.current[field];
          // Probably needs to support async / promise validation in case api call needs made
          // to perform validation.
          const error = fieldRef.onValidate({
            field,
            value: values[field],
            validation: fieldRef.validation
          });

          if (error.length) {
            acc[field] = error;
          }

          return acc;
        }, {});

      setErrors(formErrors);

      const isValid =
        Object.values(formErrors).filter(ar => ar.length > 0).length === 0;

      return isValid;
    },
    [fieldsRef, values]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      const isValid = validateForm();
      if (isValid) {
        props.onSubmit({ values });
      }
    },
    [validateForm, props, values]
  );

  const handleOnChange = useCallback(
    ({ field, value, callback }) => {
      setValues(prev => ({ ...prev, [field]: value }));

      // Clear error if there is one
      if (errors[field]?.length > 0) {
        setErrors(deleteProperty(field, errors));
      }
      if (!!callback && callback instanceof Function) {
        const fieldRef = fieldsRef.current[field];
        const error = fieldRef?.onValidate
          ? fieldRef.onValidate({
              field,
              value,
              validation: fieldRef.validation
            })
          : null;
        callback({ error });
      }
    },
    [errors]
  );

  const handleOnBlur = useCallback(({ field, value, callback }) => {
    const fieldRef = fieldsRef.current[field];

    if (fieldRef?.onValidate) {
      const error = fieldRef.onValidate({
        field,
        value,
        validation: fieldRef.validation
      });
      if (error.length) {
        setErrors(prev => ({ ...prev, [field]: error }));
      }
      if (!!callback && callback instanceof Function) callback({ error });
    }
  }, []);

  // TODO: Potentially make this return a promise so that it can easily be chained by parent via .then?
  // TODO: min/max length validation. `pattern` will need to be handled by components/consumers themselves because
  // we wouldn't be able to know what error message to show here.
  const handleOnValidate = useCallback(({ field, value, validation }) => {
    const fldLabel = fieldsRef.current[field].label || field;
    const errors = [];
    if (!validation) return errors;

    // value.length == 0 covers cases where value is an array
    if (validation.required) {
      const requiredError =
        validation?.requiredError || `${fldLabel} is required.`;
      let requiredMissing = false;
      // if is a string, trim it and check if it's empty
      if (typeof value === "string") {
        value = value.trim();
        if (value.length === 0) requiredMissing = true;
      }
      // if is boolean, check if it's false
      if (typeof value === "boolean") {
        if (!value) requiredMissing = true;
      }
      // if it's an array, check if it's empty
      if (Array.isArray(value)) {
        if (value.length === 0) requiredMissing = true;
      }
      if (requiredMissing) errors.push(requiredError);
    }

    return errors;
  }, []);

  // whenever error are updated or values are changed,
  // update the list of fields that are not valid
  // and udpate the invalidFields state so the form can know the status of all
  // fields in one spot
  useEffect(() => {
    if (values) {
      const flds = Object.keys(values)
        .filter(
          f =>
            fieldsRef?.current?.[f]?.onValidate({
              field: f,
              value: values[f],
              validation: fieldsRef.current[f].validation
            }).length > 0
        )
        .map(f => {
          return {
            name: f,
            label: fieldsRef.current[f].label,
            errors: fieldsRef.current[f].onValidate({
              field: f,
              value: values[f],
              validation: fieldsRef.current[f].validation
            })
          };
        });

      const newFields = flds.map(f => f.name);
      const prevInvalidFields = invalidFields.map(f => f.name);

      if (!newFields.equals(prevInvalidFields)) {
        setInvalidFields(flds);
      }
    }
  }, [errors, invalidFields, values]);

  /*
    Update form values and/or errors.

    If errors are not arrays or are empty arrays,
    remove the error for that field.
  */
  const updateForm = useCallback(
    form => {
      let removedFields = [];
      if (form.values) {
        if (form.replaceValues) {
          setValues(form.values);
          // set the removed fields so we can clean up errors and unregister fields
          removedFields = Object.keys(values).filter(
            k => !Object.keys(form.values).includes(k)
          );
        } else {
          setValues({ ...values, ...form.values });
        }
      }
      if (form.errors || removedFields.length) {
        const newErrors = form.replaceErrors
          ? { ...form.errors }
          : { ...errors, ...form.errors };
        Object.keys(newErrors).forEach(k => {
          if (
            !Array.isArray(newErrors[k]) ||
            !newErrors[k].length ||
            removedFields.includes(k)
          )
            delete newErrors[k];
        });
        setErrors(newErrors);
      }
      if (removedFields.length) {
        // unregister fields that are no longer in the form
        removedFields.forEach(field => {
          if (values[field] === undefined) {
            unregisterField({ field });
          }
        });
      }
    },
    [errors, unregisterField, values]
  );

  return useMemo(
    () => ({
      values,
      errors,
      handleSubmit,
      handleOnChange,
      handleOnBlur,
      handleOnValidate,
      registerField,
      unregisterField,
      updateForm,
      invalidFields,
      validateForm,
      validateFields,
      removeField
    }),
    [
      values,
      errors,
      handleSubmit,
      handleOnChange,
      handleOnBlur,
      handleOnValidate,
      registerField,
      unregisterField,
      updateForm,
      invalidFields,
      validateForm,
      validateFields,
      removeField
    ]
  );
};

/*
--------------
   useField
--------------
*/

export const useField = ({ name, validation, onValidate, label }) => {
  // Performance downside is that any error within the parent FormGroup
  // forces a re-render that we can't prevent.
  // useFormGroup(name);

  const { registerField, unregisterField } = useContext(FormContext);

  useEffect(() => {
    if (name) {
      registerField({
        field: name,
        validation,
        onValidate,
        label
      });
    }

    return () => {
      if (name) {
        unregisterField({ field: name });
      }
    };
  }, [label, name, onValidate, registerField, unregisterField, validation]);
};

Array.prototype.equals = function (array) {
  // if the other array is a falsy value, return
  if (!array) return false;

  // compare lengths - can save a lot of time
  if (this.length !== array.length) return false;

  for (let i = 0, l = this.length; i < l; i++) {
    // Check if we have nested arrays
    if (this[i] instanceof Array && array[i] instanceof Array) {
      // recurse into the nested arrays
      if (!this[i].equals(array[i])) return false;
    } else if (this[i] !== array[i]) {
      // Warning - two different object instances will never be equal: {x:20} != {x:20}
      return false;
    }
  }
  return true;
};
// Hide method from for-in loops
Object.defineProperty(Array.prototype, "equals", { enumerable: false });
